// Reading what POST /diagnoses, /phenotypes and /genotypes send back, and applying
// it to the person the chart DRAWS.
//
// Shared by the legend drop, the sidebar's Diseases / Phenotypes / Genotypes
// sections and the bulk edit, which had each spelled this for themselves — and
// two of them spelled it wrong (see savedListFrom).

/**
 * The person's FULL list of `field` from a record route's POST response, or null.
 *
 * The routes do not name it the same way: /diagnoses returns `allDiagnoses` (its
 * `diagnoses` is only what was just added), while /phenotypes and /genotypes
 * return the full list as `phenotypes` / `genotypes`. The bulk edit read
 * `allPhenotypes` / `allGenotypes`, which no route sends, so a bulk-added
 * phenotype or genotype never reached memberRecords — and the next wholesale
 * pedigree save wrote the stale arrays back over the person, erasing what had
 * just been added.
 */
export function savedListFrom(field, res) {
  const key = field === 'diagnoses' ? 'allDiagnoses' : field;
  const list = res?.data?.[key];
  return Array.isArray(list) ? list : null;
}

/**
 * The drawn person's list after a write that added `addedIds`: their OWN list,
 * with the server's copy of each added record in place of any same-id entry.
 *
 * WHY NOT JUST TAKE THE SERVER'S LIST. The record routes find a person by
 * `{ familyMemberId, user_id }`. `familyMemberId` is queryable-encrypted, so its
 * unique index cannot fire, and a family can hold more than one row with the same
 * id — while the chart draws, and the wholesale pedigree save writes, the row
 * found by `_id`. When the POST lands on a different row than the one drawn, the
 * list it returns is THAT row's. Copying it onto the drawn person, and then
 * saving, replaced their records with somebody else's: the new record arrived
 * and everything they already had was lost. Merging takes only what this write
 * added, so the drawn person always ends up with exactly their own records plus
 * it, whichever row the server wrote to. In the ordinary case — one row — the
 * result is the same list the server returned.
 *
 * @param {object[]} rowList   the drawn person's current list
 * @param {object[]} savedList the list the route returned
 * @param {string[]} addedIds  ids this write added
 * @returns {object[]}
 */
export function mergeSavedRecords(rowList, savedList, addedIds) {
  const ids = new Set(addedIds || []);
  const saved = new Map();
  (savedList || []).forEach((r) => {
    if (r && ids.has(r.id)) saved.set(r.id, r);
  });
  return [
    ...(rowList || []).filter((r) => !saved.has(r?.id)),
    ...saved.values()
  ];
}
